import React, { useState, useRef } from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Input from '@mui/joy/Input';
import Textarea from '@mui/joy/Textarea';
import IconButton from '@mui/joy/IconButton';

import Switch from '@mui/joy/Switch';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';

import { themeColors, shadows } from '../../styles/theme';

export default function TestRunModal({
  open,
  onClose,
  onStartTestRun,
  isRunning,
  isStateful = false,
}) {
  const [jobName, setJobName] = useState('Test Run');
  const [transcripts, setTranscripts] = useState([{ transcript: '', author: '', name: '', is_processed: false }]);
  const [stateJson, setStateJson] = useState('');
  const [stateJsonError, setStateJsonError] = useState(null);
  const [stateHistoryJson, setStateHistoryJson] = useState('');
  const [stateHistoryJsonError, setStateHistoryJsonError] = useState(null);
  const [photos, setPhotos] = useState([]); // [{ name, dataUrl }]
  const photoInputRef = useRef(null);

  const handlePhotoFiles = (fileList) => {
    Array.from(fileList).forEach((file) => {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = () => {
        setPhotos((prev) => [...prev, { name: file.name, dataUrl: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const addTranscript = () => {
    setTranscripts([...transcripts, { transcript: '', author: '', name: '', is_processed: false }]);
  };

  const removeTranscript = (index) => {
    setTranscripts(transcripts.filter((_, i) => i !== index));
  };

  const updateTranscript = (index, field, value) => {
    setTranscripts(transcripts.map((t, i) => i === index ? { ...t, [field]: value } : t));
  };

  const handleSubmit = () => {
    const validTranscripts = transcripts
      .filter((t) => t.transcript.trim().length > 0)
      .map((t) => ({
        transcript: t.transcript.trim(),
        author: t.author.trim() || 'anonymous',
        name: t.name.trim(),
        datetime: new Date().toISOString(),
        is_processed: t.is_processed,
      }));
    if (validTranscripts.length === 0) return;

    let parsedState = {};
    if (stateJson.trim()) {
      try {
        parsedState = JSON.parse(stateJson.trim());
        setStateJsonError(null);
      } catch (e) {
        setStateJsonError('Invalid JSON');
        return;
      }
    }

    let parsedStateHistory = [];
    if (isStateful && stateHistoryJson.trim()) {
      try {
        parsedStateHistory = JSON.parse(stateHistoryJson.trim());
        setStateHistoryJsonError(null);
      } catch (e) {
        setStateHistoryJsonError('Invalid JSON');
        return;
      }
    }

    const photoPayload = photos.map((p, index) => ({ image_url: p.dataUrl, order: index }));

    onStartTestRun(validTranscripts, jobName, parsedState, parsedStateHistory, photoPayload);
  };

  const isEmpty = transcripts.every((t) => t.transcript.trim().length === 0);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          width: 600,
          maxHeight: '85vh',
          border: 'none',
          boxShadow: shadows.xl,
          borderRadius: 2.5,
          p: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            pb: 2.5,
            borderBottom: `1px solid ${themeColors.neutral[100]}`,
            flexShrink: 0,
          }}
        >
          <ModalClose
            sx={{
              top: 16,
              right: 16,
              width: 32,
              height: 32,
              backgroundColor: themeColors.neutral[100],
              border: 'none',
              '&:hover': {
                backgroundColor: themeColors.neutral[200],
              },
            }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
                boxShadow: `0 4px 12px ${themeColors.primary[500]}30`,
              }}
            >
              <PlayArrowIcon sx={{ fontSize: 22, color: 'white' }} />
            </Box>

            <Box>
              <Typography level="h4" sx={{ fontWeight: 700, color: themeColors.neutral[900] }}>
                Test Run
              </Typography>
              <Typography level="body-sm" sx={{ color: themeColors.neutral[600], fontSize: '0.8125rem' }}>
                Run your flow with sample data
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, overflow: 'auto', flex: 1 }}>
          {/* Job Name */}
          <Box>
            <Typography
              level="body-sm"
              sx={{ fontWeight: 600, color: themeColors.neutral[800], mb: 0.75, fontSize: '0.8125rem' }}
            >
              Job Name
            </Typography>
            <Input
              value={jobName}
              onChange={(e) => setJobName(e.target.value)}
              placeholder="Test Run"
              sx={{
                fontSize: '0.875rem',
                borderColor: themeColors.neutral[200],
                '&:focus-within': {
                  borderColor: themeColors.primary[400],
                  boxShadow: shadows.focus,
                },
              }}
            />
            <Typography
              level="body-xs"
              sx={{ color: themeColors.neutral[500], mt: 0.5, fontSize: '0.75rem' }}
            >
              Available as {'{{JobName}}'} in your prompts
            </Typography>
          </Box>

          {/* Transcripts */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography
                level="body-sm"
                sx={{ fontWeight: 600, color: themeColors.neutral[800], fontSize: '0.8125rem' }}
              >
                Transcripts
              </Typography>
              <Button
                variant="plain"
                size="sm"
                startDecorator={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={addTranscript}
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: themeColors.primary[600],
                  '&:hover': {
                    backgroundColor: `${themeColors.primary[500]}08`,
                  },
                }}
              >
                Add Transcript
              </Button>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                maxHeight: '45vh',
                overflow: 'auto',
                pr: 0.5,
              }}
            >
              {transcripts.map((t, index) => (
                <Box
                  key={index}
                  sx={{
                    border: `1px solid ${themeColors.neutral[200]}`,
                    borderRadius: 2,
                    p: 2,
                    position: 'relative',
                    transition: 'border-color 0.15s ease',
                    '&:focus-within': {
                      borderColor: themeColors.primary[300],
                    },
                  }}
                >
                  {/* Header row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography
                      level="body-xs"
                      sx={{ fontWeight: 700, color: themeColors.neutral[500], fontSize: '0.6875rem', letterSpacing: '0.5px' }}
                    >
                      TRANSCRIPT {index + 1}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography level="body-xs" sx={{ color: themeColors.neutral[500], fontSize: '0.6875rem' }}>
                          is_processed
                        </Typography>
                        <Switch
                          size="sm"
                          checked={t.is_processed}
                          onChange={(e) => updateTranscript(index, 'is_processed', e.target.checked)}
                          sx={{
                            '--Switch-trackWidth': '28px',
                            '--Switch-trackHeight': '16px',
                            '--Switch-thumbSize': '12px',
                          }}
                        />
                      </Box>
                      {transcripts.length > 1 && (
                        <IconButton
                          size="sm"
                          variant="plain"
                          onClick={() => removeTranscript(index)}
                          sx={{
                            width: 24,
                            height: 24,
                            minWidth: 24,
                            minHeight: 24,
                            color: themeColors.neutral[400],
                            '&:hover': {
                              color: themeColors.danger,
                              backgroundColor: `${themeColors.danger}08`,
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {/* Transcript content */}
                  <Textarea
                    value={t.transcript}
                    onChange={(e) => updateTranscript(index, 'transcript', e.target.value)}
                    placeholder="Paste transcript text here..."
                    minRows={4}
                    maxRows={10}
                    sx={{
                      fontSize: '0.8125rem',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      borderColor: themeColors.neutral[150] || themeColors.neutral[200],
                      mb: 1.5,
                      '&:focus-within': {
                        borderColor: themeColors.primary[400],
                        boxShadow: shadows.focus,
                      },
                    }}
                  />

                  {/* Author + Name row */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Input
                      value={t.name}
                      onChange={(e) => updateTranscript(index, 'name', e.target.value)}
                      placeholder="Display name"
                      size="sm"
                      startDecorator={
                        <Typography level="body-xs" sx={{ color: themeColors.neutral[500], fontSize: '0.75rem', fontWeight: 500 }}>
                          Name:
                        </Typography>
                      }
                      sx={{
                        flex: 1,
                        fontSize: '0.8125rem',
                        borderColor: themeColors.neutral[200],
                        '&:focus-within': {
                          borderColor: themeColors.primary[400],
                          boxShadow: shadows.focus,
                        },
                      }}
                    />
                    <Input
                      value={t.author}
                      onChange={(e) => updateTranscript(index, 'author', e.target.value)}
                      placeholder="anonymous"
                      size="sm"
                      startDecorator={
                        <Typography level="body-xs" sx={{ color: themeColors.neutral[500], fontSize: '0.75rem', fontWeight: 500 }}>
                          Author:
                        </Typography>
                      }
                      sx={{
                        flex: 1,
                        fontSize: '0.8125rem',
                        borderColor: themeColors.neutral[200],
                        '&:focus-within': {
                          borderColor: themeColors.primary[400],
                          boxShadow: shadows.focus,
                        },
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Photos */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography
                level="body-sm"
                sx={{ fontWeight: 600, color: themeColors.neutral[800], fontSize: '0.8125rem' }}
              >
                Photos
              </Typography>
              <Button
                variant="plain"
                size="sm"
                startDecorator={<AddPhotoAlternateOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={() => photoInputRef.current?.click()}
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: themeColors.primary[600],
                  '&:hover': {
                    backgroundColor: `${themeColors.primary[500]}08`,
                  },
                }}
              >
                Add Photos
              </Button>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                  handlePhotoFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </Box>

            {photos.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {photos.map((photo, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                      width: 72,
                      height: 72,
                      borderRadius: 1.5,
                      overflow: 'hidden',
                      border: `1px solid ${themeColors.neutral[200]}`,
                    }}
                  >
                    <Box
                      component="img"
                      src={photo.dataUrl}
                      alt={photo.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    <IconButton
                      size="sm"
                      variant="solid"
                      onClick={() => removePhoto(index)}
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        width: 18,
                        height: 18,
                        minWidth: 18,
                        minHeight: 18,
                        backgroundColor: 'rgba(0,0,0,0.55)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 12, color: 'white' }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography level="body-xs" sx={{ color: themeColors.neutral[500], fontSize: '0.75rem' }}>
                Optional — provided to the Photo node; sent to AI nodes as vision input when connected
              </Typography>
            )}
          </Box>

          {/* Initial State */}
          <Box>
            <Typography
              level="body-sm"
              sx={{ fontWeight: 600, color: themeColors.neutral[800], mb: 0.75, fontSize: '0.8125rem' }}
            >
              Initial State (JSON)
            </Typography>
            <Textarea
              value={stateJson}
              onChange={(e) => { setStateJson(e.target.value); setStateJsonError(null); }}
              placeholder="{}"
              minRows={3}
              maxRows={8}
              sx={{
                fontSize: '0.8125rem',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                borderColor: stateJsonError ? themeColors.danger : themeColors.neutral[200],
                '&:focus-within': {
                  borderColor: stateJsonError ? themeColors.danger : themeColors.primary[400],
                  boxShadow: stateJsonError ? 'none' : shadows.focus,
                },
              }}
            />
            {stateJsonError && (
              <Typography level="body-xs" sx={{ color: themeColors.danger, mt: 0.5, fontSize: '0.75rem' }}>
                {stateJsonError}
              </Typography>
            )}
            <Typography level="body-xs" sx={{ color: themeColors.neutral[500], mt: 0.5, fontSize: '0.75rem' }}>
              Simulates existing state — available as {'{{state.*}}'} / get_state()
            </Typography>
          </Box>

          {/* State History — only for stateful flows */}
          {isStateful && (
            <Box>
              <Typography
                level="body-sm"
                sx={{ fontWeight: 600, color: themeColors.neutral[800], mb: 0.75, fontSize: '0.8125rem' }}
              >
                State History (JSON)
              </Typography>
              <Textarea
                value={stateHistoryJson}
                onChange={(e) => { setStateHistoryJson(e.target.value); setStateHistoryJsonError(null); }}
                placeholder="[]"
                minRows={3}
                maxRows={8}
                sx={{
                  fontSize: '0.8125rem',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  borderColor: stateHistoryJsonError ? themeColors.danger : themeColors.neutral[200],
                  '&:focus-within': {
                    borderColor: stateHistoryJsonError ? themeColors.danger : themeColors.primary[400],
                    boxShadow: stateHistoryJsonError ? 'none' : shadows.focus,
                  },
                }}
              />
              {stateHistoryJsonError && (
                <Typography level="body-xs" sx={{ color: themeColors.danger, mt: 0.5, fontSize: '0.75rem' }}>
                  {stateHistoryJsonError}
                </Typography>
              )}
              <Typography level="body-xs" sx={{ color: themeColors.neutral[500], mt: 0.5, fontSize: '0.75rem' }}>
                Simulates past state ops — available via state_history_node
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            pt: 2.5,
            borderTop: `1px solid ${themeColors.neutral[100]}`,
            display: 'flex',
            gap: 1.5,
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          <Button
            variant="plain"
            onClick={onClose}
            sx={{
              color: themeColors.neutral[700],
              fontWeight: 500,
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: themeColors.neutral[100],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isEmpty || isRunning}
            startDecorator={<PlayArrowIcon sx={{ fontSize: 18 }} />}
            sx={{
              background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.primary[600]})`,
              boxShadow: `0 2px 8px ${themeColors.primary[500]}30`,
              fontWeight: 600,
              fontSize: '0.875rem',
              px: 3,
              '&:hover': {
                boxShadow: `0 4px 12px ${themeColors.primary[500]}40`,
              },
              '&:disabled': {
                opacity: 0.5,
              },
            }}
          >
            Start Test Run
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
